import { AppDispatch, RootState } from '@/app/globalRedux/store'
import { ListType } from '@/app/types/constantDataType'
import ImageUploader from '@/components/ImageUploader'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  colourStyles,
  generateValueLabelFormate,
} from '@/components/ui/select/selectStyle'
import {
  getAdPosition,
  getPageForAdvertisement,
  sendAdvertiseRequest,
} from '@/services/AdService'
import {
  extractValues,
  fetchUrlAndConvertToFiles,
  getCuratedLanguageList,
} from '@/services/generalDataServices'
import { TextField } from '@mui/material'
import { Label } from '@radix-ui/react-label'
import Image from 'next/image'
import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Select from 'react-select'
import { toast } from 'sonner'
import SingleSelect from 'react-select'
import ReCAPTCHA from 'react-google-recaptcha'
import RecaptchaV2React from '@/app/[lng]/components/Validation/RecaptchaV2React'
import { useRecaptchaValidator } from '@/app/hooks/useRecaptchaV2'

interface AdvertiseFormData {
  language: string
  languageId: any
  page: string
  pageId: any
  startDate: string | any
  endDate: string | any
  images: any[]
}

const advertiseFormInitialState = {
  language: '',
  languageId: -1,
  page: '',
  pageId: -1,
  startDate: '',
  endDate: '',
  images: [],
}

const AdvertiseForm = () => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useDispatch()
  const recaptchaRef = useRef<ReCAPTCHA | null>(null)
  const { validateRecaptcha } = useRecaptchaValidator(recaptchaRef)

  const curatedLanguages = useSelector(
    (state: RootState) => state.constantData.curatedLanguages
  )
  const isLoggedIn = useSelector((state: RootState) => state?.user?.loggedIn)
  const [openAdvertisementForm, setOpenAdvertisementForm] =
    useState<boolean>(false)
  const [adPosition, setAdPosition] = useState<ListType[]>([])

  const [advertiseFormData, setAdvertiseMentFormData] =
    useState<AdvertiseFormData>(advertiseFormInitialState)
  const [image, setImage] = useState<string[]>([])
  const [selectedAdPostion, setSelectedAdPosition] = useState<string[]>([])
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false)
  const [pages, setPages] = useState<
    {
      id: number
      name: string
    }[]
  >([])

  const setAdvertiseMentFormController = () => {
    setFormSubmitted(false)
    setOpenAdvertisementForm(!openAdvertisementForm)
    setFormSubmitted(false)
    setAdvertiseMentFormData(advertiseFormInitialState)
    setImage([])
    setSelectedAdPosition([])
  }

  const handleSubmit = async () => {
    setFormSubmitted(true)
    if (
      advertiseFormData?.startDate == '' ||
      advertiseFormData?.endDate == '' ||
      advertiseFormData?.pageId == -1 ||
      image?.length == 0 ||
      selectedAdPostion?.length == 0 ||
      advertiseFormData?.languageId == -1
    ) {
      return
    }

    const isRecaptchaVerified = await validateRecaptcha()
    if (!isRecaptchaVerified) return

    const formData = await createFormData()
    const response: { status: string } = await sendAdvertiseRequest(formData)
    if (response.status == 'success') {
      toast.success(t('Advertise Request Sent SuccessFully'))
      setOpenAdvertisementForm(false)
      setAdvertiseMentFormData(advertiseFormInitialState)
      setImage([])
      setSelectedAdPosition([])
    } else if (response.status == 'failure') {
      toast.success(t('Error in Sending Advertisement Form'))
      setOpenAdvertisementForm(false)
      setFormSubmitted(false)
    }
  }

  const createFormData = async () => {
    const formData = new FormData()
    formData.append(
      'banner_schedule[start_date]',
      String(new Date(advertiseFormData?.startDate))
    )
    formData.append(
      'banner_schedule[end_date]',
      String(new Date(advertiseFormData?.endDate))
    )
    formData.append('banner_schedule[page_id]', advertiseFormData?.pageId)

    selectedAdPostion.forEach((ad) => {
      formData.append('banner_schedule[ad_position_ids][]', ad)
    })
    if (image) {
      let img: File[]
      if (Array.isArray(image)) {
        img = await fetchUrlAndConvertToFiles(image)
      } else {
        img = image
      }
      const imageFile: File[] = img
      if (imageFile) {
        for (let i = 0; i < imageFile.length; i++) {
          const file = imageFile[i]
          formData.append(
            'banner_schedule[advertisement_request_images_attributes][][image]',
            file
          )
        }
      }
    }
    formData.append(
      'banner_schedule[language_id]',
      advertiseFormData?.languageId
    )

    return formData
  }

  const fetchPages = async () => {
    const response = await getPageForAdvertisement()
    setPages(response)
  }

  useEffect(() => {
    if (curatedLanguages?.length == 0) {
      dispatch(getCuratedLanguageList())
    }
    if (pages?.length <= 0) {
      fetchPages()
    }
  }, [])

  useEffect(() => {
    const fetchAdPosition = async () => {
      const response = await getAdPosition(advertiseFormData?.pageId)
      setAdPosition(response)
    }
    if (advertiseFormData?.pageId !== -1) {
      fetchAdPosition()
    }
  }, [advertiseFormData?.pageId])
  if (!isLoggedIn) {
    return (
      <Button variant="secondary">
        <a className="cursor-pointer" href="mailto:cd@fructidor.com">
          {t('ADVERTISE')}
        </a>
      </Button>
    )
  }
  return (
    <div>
      <Dialog
        open={openAdvertisementForm}
        onOpenChange={setAdvertiseMentFormController}
      >
        <DialogTrigger asChild>
          <Button variant="secondary" className="mobile:w-full">
            {t('Advertise')}
          </Button>
        </DialogTrigger>
        <DialogContent className="!max-w-[940px] overflow-y-scroll max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="bg-modal_header px-8 py-4 tracking-wide flex items-center gap-8 ">
              {t('Add Advertisement')}
            </DialogTitle>
          </DialogHeader>
          <div className="p-8 pt-6 flex flex-col gap-y-6">
            {/* 1st row */}
            <div className="flex flex-row w-full gap-6 mobile-width mobile:flex-col">
              <div className="w-[50%] flex flex-col gap-2 mobile:w-[100%] relative">
                <Label className="required">{t('Page')}</Label>
                <SingleSelect
                  options={generateValueLabelFormate(pages)}
                  onChange={(option: any) =>
                    setAdvertiseMentFormData({
                      ...advertiseFormData,
                      pageId: option?.value,
                      page: option?.label,
                    })
                  }
                  placeholder={t('Select Page')}
                  styles={colourStyles}
                  menuPlacement="auto"
                />
                {formSubmitted && advertiseFormData.pageId == -1 && (
                  <div className="text-required_color absolute text-xs -bottom-5">
                    {t('Need Page For Submission')}
                  </div>
                )}
              </div>
              <div className="relative w-[50%] flex flex-col gap-2 mobile:w-[100%]">
                <Label className="required">{t('Language')}</Label>
                <SingleSelect
                  options={generateValueLabelFormate(curatedLanguages)}
                  onChange={(option: any) =>
                    setAdvertiseMentFormData({
                      ...advertiseFormData,
                      language: option?.label,
                      languageId: option?.value,
                    })
                  }
                  placeholder={t('Select language for the ad to be displayed')}
                  styles={colourStyles}
                  menuPlacement="auto"
                />
                {formSubmitted && advertiseFormData?.languageId == -1 && (
                  <div className="text-required_color absolute text-xs -bottom-5">
                    {t('Need Language For Submission')}
                  </div>
                )}
              </div>
            </div>
            {/* 2nd row */}

            <div className=" flex flex-row gap-6 mobile-width mobile:flex-col">
              <div className="w-[50%] flex flex-col gap-1 relative mobile:w-[100%]">
                <Label className="required">{t('Ad Position')}</Label>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  options={generateValueLabelFormate(adPosition)}
                  onChange={(e) => {
                    setSelectedAdPosition(extractValues(Object.values(e)))
                  }}
                  placeholder={t('Select Ad Position')}
                  className="ReactSelect"
                  hideSelectedOptions
                  styles={colourStyles}
                />
                {formSubmitted && selectedAdPostion?.length == 0 && (
                  <div
                    className={`text-required_color absolute text-xs -bottom-5`}
                  >
                    {t('Need Ad Position For Submission')}
                  </div>
                )}
              </div>
              <div className="relative w-[50%] mobile:w-[100%]">
                <div className=" flex flex-row w-full gap-5">
                  <div className="flex flex-col w-[50%] gap-1">
                    <Label className="required">{t('Start Date')}</Label>
                    <TextField
                      type="date"
                      InputProps={{
                        inputProps: {
                          min: new Date().toISOString().split('T')[0],
                        },
                      }}
                      size="small"
                      value={advertiseFormData?.startDate}
                      className=" flex !cursor-pointer"
                      onChange={(e) => {
                        setAdvertiseMentFormData({
                          ...advertiseFormData,
                          startDate: e?.target?.value,
                        })
                      }}
                    />
                  </div>
                  <div className="flex flex-col w-[50%] gap-1">
                    <Label className="required">{t('End Date')}</Label>
                    <TextField
                      type="date"
                      size="small"
                      className="flex !cursor-pointer"
                      value={advertiseFormData?.endDate}
                      InputProps={{
                        inputProps: {
                          min: advertiseFormData?.startDate,
                        },
                      }}
                      onChange={(e) => {
                        setAdvertiseMentFormData({
                          ...advertiseFormData,
                          endDate: e?.target?.value,
                        })
                      }}
                    />
                  </div>
                </div>
                {formSubmitted &&
                  advertiseFormData?.startDate >=
                    advertiseFormData?.endDate && (
                    <div className="text-required_color text-xs absolute -bottom-5 ">
                      {t('Start Date Needs to be greater than End date')}
                    </div>
                  )}
              </div>
            </div>
            {/* 3rd row */}

            <div className="relative w-full mobile-width">
              <Label className="required">{t('Image')}</Label>
              <div className="flex gap-[40px] w-full">
                <div className="flex gap-[20px] w-full">
                  <div className=" flex flex-row w-full gap-[10px]  ">
                    <div className=" w-[48%] mobile:w-[100%]">
                      <ImageUploader
                        imageArray={advertiseFormData?.images}
                        setImage={setImage}
                      />
                      {formSubmitted && image?.length == 0 && (
                        <div className="text-required_color text-xs mt-1">
                          {t('Need Atleast 1 Image Submission')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <p className=" text-[#afafaf] mt-5 font-light text-[14px] mobile:mt-7">
                {t('Fructidor team will contact you briefly')}
              </p>
            </div>
            <RecaptchaV2React ref={recaptchaRef} />
          </div>
          <DialogFooter className="sm:justify-center">
            <Button
              size="icon"
              variant="secondary"
              onClick={() => {
                handleSubmit()
              }}
            >
              <Image
                alt="send"
                src={'/assets/Icons/sendIcon.svg'}
                height={14.2}
                width={14.2}
              />
              {t('SEND')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AdvertiseForm
